import { isValidPhoneNumber } from 'libphonenumber-js';
import { z } from 'zod';

import { isValidCNPJ } from './cnpj';
import { isValidCPF } from './cpf';
import { isLikelyValidName, PERSON_NAME_REGEX } from './functions';

export const DOCUMENT_TYPE_OPTIONS = ['cnh', 'rg', 'cin', 'passport'] as const;
export type DocumentType = (typeof DOCUMENT_TYPE_OPTIONS)[number];
export const documentTypeValidator = z.enum(DOCUMENT_TYPE_OPTIONS, {
  message: 'Tipo de documento deve ser selecionado'
});

export const SKIPPABLE_DOCUMENT_STATUSES = ['valid', 'review', 'pending'] as const;

export const DOCUMENT_FILE_MODE_OPTIONS = ['single', 'multiple'] as const;
export type DocumentFileMode = (typeof DOCUMENT_FILE_MODE_OPTIONS)[number];
export const documentFileModeValidator = z.enum(DOCUMENT_FILE_MODE_OPTIONS, {
  message: 'Selecione uma opção'
});

export const documentSchema = z
  .object({
    signerDocument: z
      .string()
      .min(1, { message: 'CPF deve ser preenchido' })
      .refine(isValidCPF, { message: 'CPF inválido' }),
    documentType: documentTypeValidator,
    documentFileMode: documentFileModeValidator,
    documentFile: z.string().min(1, { message: 'Arquivo de documento deve ser preenchido' }),
    documentFileBack: z.string().optional(),
    documentStatus: z.enum(SKIPPABLE_DOCUMENT_STATUSES)
  })
  .refine(data => (data.documentFileMode === 'multiple' ? !!data.documentFileBack : true), {
    message: 'Arquivo do verso é obrigatório',
    path: ['documentFileBack']
  });

export const personTypeSchema = z.enum(['F', 'J'], { message: 'Tipo de pessoa deve ser selecionado' });

export const cpfCnpjSchema = z
  .string()
  .min(1, { message: 'Documento deve ser preenchido' })
  .refine(value => isValidCPF(value) || isValidCNPJ(value), { message: 'Documento deve ser um CPF ou CNPJ válido' });

export const documentCaptureSchema = z.object({
  document: cpfCnpjSchema
});

export const pepSchema = z.object({
  pep: z.preprocess(
    value => {
      if (value !== 'true' && value !== 'false') {
        return value;
      }

      if (value === 'true') {
        return true;
      }

      return false;
    },
    z.boolean({
      message: 'Selecione uma opção.'
    })
  )
});

export const INCOME_RANGE_OPTIONS = ['until-1500', '1500-3000', '3000-5000', '5000-10000', 'over-10000'] as const;
export type IncomeRange = (typeof INCOME_RANGE_OPTIONS)[number];
export const incomeRangeValidator = z.enum(INCOME_RANGE_OPTIONS, {
  message: 'Renda deve ser selecionada'
});

export const LEGAL_ENTITY_INCOME_RANGE_OPTIONS = [
  'until-81k',
  'until-360k',
  'until-4800k',
  '4800k-50000k',
  'over-50000k'
] as const;
export type LegalEntityIncomeRange = (typeof LEGAL_ENTITY_INCOME_RANGE_OPTIONS)[number];
export const legalEntityIncomeRangeValidator = z.enum(LEGAL_ENTITY_INCOME_RANGE_OPTIONS, {
  message: 'Renda da empresa deve ser selecionada'
});

export const incomeSchema = z.object({
  income: incomeRangeValidator
});

export const legalEntityIncomeSchema = z.object({
  legalEntityIncome: legalEntityIncomeRangeValidator
});

export const representativeDocumentFileSchema = z.object({
  representativeDocumentFile: z.string().regex(/^[a-f0-9]{64}$/, 'Arquivo de documento do representante é obrigatório'),
  representativeDocumentStatus: z.enum(SKIPPABLE_DOCUMENT_STATUSES)
});

export const socialContractFileItemSchema = z.object({
  hash: z.string().regex(/^[a-f0-9]{64}$/),
  fileName: z.string().min(1)
});

export type SocialContractFileItem = z.infer<typeof socialContractFileItemSchema>;

export const socialContractSchema = z.object({
  socialContractFiles: z
    .array(socialContractFileItemSchema)
    .min(1, 'Pelo menos um arquivo de contrato social deve ser enviado'),
  socialContractStatus: z.enum(SKIPPABLE_DOCUMENT_STATUSES)
});

export const termsSchema = z.object({
  fidcTerms: z.literal(true, {
    error: 'Aceite os termos de Cessão ao FIDC.'
  }),
  useTerms: z.literal(true, {
    error: 'Aceite os termos de uso Eduzz.'
  }),
  fiscalResponsibility: z.literal(true, {
    error: 'Aceite o termo de responsabilidade fiscal.'
  }),
  infoTruthfulness: z.literal(true, {
    error: 'Confirme que as informações fornecidas são verídicas e estão atualizadas.'
  })
});

export const occupationSchema = z.object({
  occupation: z
    .string()
    .min(1, { message: 'Ocupação deve ser preenchida' })
    .max(100, { message: 'Ocupação deve ter no máximo 100 caracteres' })
});

export const faceDetectionSchema = z.object({
  faceId: z.string().min(1, { message: 'Reconhecimento facial é obrigatório' })
});

export const addressSchema = z
  .object({
    cep: z
      .string()
      .trim()
      .regex(/^\d{8}$/, { message: 'CEP deve conter 8 dígitos' }),
    stateName: z.string().trim().min(1, { message: 'Estado deve ser preenchido' }),
    stateCode: z.string().trim().min(1, { message: 'Estado deve ser preenchido' }),
    cityName: z.string().trim().min(1, { message: 'Cidade deve ser preenchida' }),
    cityCode: z.string().trim().min(1, { message: 'Cidade deve ser preenchida' }),
    neighborhood: z
      .string()
      .trim()
      .min(1, { message: 'Bairro deve ser preenchido' })
      .max(100, { message: 'Bairro deve ter no máximo 100 caracteres' }),
    street: z
      .string()
      .trim()
      .min(1, { message: 'Rua deve ser preenchida' })
      .max(200, { message: 'Rua deve ter no máximo 200 caracteres' }),
    number: z.string().max(10, { message: 'Número deve ter no máximo 10 caracteres' }).optional(),
    noNumber: z.boolean().optional(),
    complement: z.string().max(100, { message: 'Complemento deve ter no máximo 100 caracteres' }).optional(),
    reference: z.string().max(200, { message: 'Ponto de referência deve ter no máximo 200 caracteres' }).optional()
  })
  .check(ctx => {
    if (!ctx.value.noNumber && !ctx.value.number) {
      ctx.issues.push({
        code: 'custom',
        message: 'Número deve ser preenchido',
        path: ['number'],
        input: ctx.value
      });
    }
  });

export const BANK_ACCOUNT_TYPE_OPTIONS = ['personal', 'company'] as const;
export type BankAccountType = (typeof BANK_ACCOUNT_TYPE_OPTIONS)[number];
export const bankAccountTypeValidator = z.enum(BANK_ACCOUNT_TYPE_OPTIONS, {
  message: 'Tipo de titularidade deve ser selecionado'
});

export const ACCOUNT_TYPE_OPTIONS = ['checking', 'savings'] as const;
export type AccountType = (typeof ACCOUNT_TYPE_OPTIONS)[number];
export const accountTypeValidator = z.enum(ACCOUNT_TYPE_OPTIONS, {
  message: 'Tipo de conta deve ser selecionado'
});

const bankAccountBaseFields = {
  fullName: z.string().min(1),
  document: z.string().min(1),
  bankAccountDocument: z.string().min(11, { message: 'Documento do titular deve ser preenchido' }),
  bank: z.coerce
    .number()
    .min(1, { message: 'Banco deve ser selecionado' })
    .max(999, { message: 'Banco deve ser selecionado' }),
  agency: z
    .string()
    .min(1, { message: 'Agência deve ser preenchida' })
    .regex(/^\d+$/, { message: 'Agência deve conter apenas números' }),
  agencyDigit: z
    .string()
    .regex(/^[\dx]?$/i, { message: 'Dígito deve ser um número ou X' })
    .nullish()
    .transform(value => value || ''),
  account: z
    .string()
    .min(1, { message: 'Conta deve ser preenchida' })
    .regex(/^\d+$/, { message: 'Conta deve conter apenas números' }),
  accountDigit: z
    .string()
    .min(1, { message: 'Dígito da conta deve ser preenchido' })
    .regex(/^[\dx]$/i, { message: 'Dígito deve ser um número ou X' }),
  accountType: accountTypeValidator
};

const personalBankAccountOwner = z
  .string()
  .min(3, { message: 'Nome do titular deve ser preenchido' })
  .regex(PERSON_NAME_REGEX, { message: 'Nome do titular deve conter apenas letras' })
  .refine(isLikelyValidName, { message: 'Nome do titular parece inválido. Informe nome e sobrenome.' });

const companyBankAccountOwner = z.string().min(1, { message: 'Nome do titular deve ser preenchido' });

export const bankAccountSchema = z.discriminatedUnion('bankAccountType', [
  z.object({
    ...bankAccountBaseFields,
    bankAccountType: z.literal('personal'),
    bankAccountOwner: personalBankAccountOwner
  }),
  z.object({
    ...bankAccountBaseFields,
    bankAccountType: z.literal('company'),
    bankAccountOwner: companyBankAccountOwner
  })
]);

export const LEGAL_ENTITY_RELATION_OPTIONS = ['none', 'partner', 'representative'] as const;
export type LegalEntityRelation = (typeof LEGAL_ENTITY_RELATION_OPTIONS)[number];

export const legalEntityRelationValidator = z.enum(LEGAL_ENTITY_RELATION_OPTIONS, {
  message: 'Relação com a empresa deve ser selecionada'
});

export const legalEntityRelationSchema = z.object({
  legalEntityRelation: legalEntityRelationValidator
});

export type LegalEntityRelationType = z.infer<typeof legalEntityRelationSchema>;

export const REGISTRY_STATUS_OPTIONS = [
  'incomplete',
  'pending',
  'review',
  'exempt',
  'valid',
  'invalid',
  'blocked'
] as const;
export type RegistryStatus = (typeof REGISTRY_STATUS_OPTIONS)[number];
export const registryStatusValidator = z.enum(REGISTRY_STATUS_OPTIONS);

export const REGISTRY_TYPE_OPTIONS = ['normal', 'update'] as const;
export type RegistryType = (typeof REGISTRY_TYPE_OPTIONS)[number];
export const registryTypeValidator = z.enum(REGISTRY_TYPE_OPTIONS);

export const REGISTRY_STATUS_LABELS: Record<RegistryStatus, string> = {
  incomplete: 'Incompleto',
  pending: 'Pendente',
  review: 'Em análise',
  exempt: 'Isento',
  valid: 'Válido',
  invalid: 'Inválido',
  blocked: 'Bloqueado'
};

export const BANK_ACCOUNT_VALIDATION_STATUS_OPTIONS = ['pending', 'valid', 'invalid', 'timeout', 'error'] as const;
export type BankAccountValidationStatus = (typeof BANK_ACCOUNT_VALIDATION_STATUS_OPTIONS)[number];
export const bankAccountValidationStatusValidator = z.enum(BANK_ACCOUNT_VALIDATION_STATUS_OPTIONS);

export const phoneSchema = z.object({
  phone: z
    .string()
    .min(1, { message: 'Telefone deve ser preenchido' })
    .refine(value => isValidPhoneNumber(value), { message: 'Telefone inválido' })
});
